// src/auth/AuthContext.jsx
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost, withAuth } from "../utils/apiClient";
import { getDefaultPartnerAlias } from "../utils/partnerMap";

const AuthContext = createContext(null);

const TOKEN_KEY = "access_token"; // sessionStorage
const PARTNER_ALIAS_KEY = "active_partner_alias"; // sessionStorage (admin only)

/* ----------------------------- STORAGE HELPERS ----------------------------- */
const readSS = (k) => sessionStorage.getItem(k);
const writeSS = (k, v) => sessionStorage.setItem(k, String(v));
const clearSS = (k) => sessionStorage.removeItem(k);

const readToken = () => readSS(TOKEN_KEY);
const writeToken = (t) => writeSS(TOKEN_KEY, t);
const clearToken = () => clearSS(TOKEN_KEY);

const readAlias = () => readSS(PARTNER_ALIAS_KEY);
const writeAlias = (v) => writeSS(PARTNER_ALIAS_KEY, v);
const clearAlias = () => clearSS(PARTNER_ALIAS_KEY);

export const AuthProvider = ({ children }) => {
	const navigate = useNavigate();

	const [token, setToken] = useState(() => readToken());
	const [user, setUser] = useState(null); // { role, partnerName, email }
	const [loading, setLoading] = useState(true);

	// Admin partner selection (UI preference)
	const [activePartnerAlias, setActivePartnerAlias] = useState(() =>
		readAlias()
	);

	const clearLocal = useCallback(() => {
		setUser(null);
		setToken(null);
		clearToken();

		setActivePartnerAlias(null);
		clearAlias();
	}, []);

	const fetchMe = useCallback(async () => {
		setLoading(true);

		const t = readToken();
		if (!t) {
			clearLocal();
			setLoading(false);
			return;
		}

		try {
			const res = await apiGet("/auth/me", {
				headers: withAuth(t),
				onUnauthorized: clearLocal
			});

			const role = res?.data?.role ?? null;
			const partnerName = res?.data?.partnerName ?? null;
			const email = res?.data?.email ?? null;

			if (!role) {
				clearLocal();
				return;
			}

			setUser({ role, partnerName, email });

			// Admin-only: ensure alias exists in sessionStorage
			if (role === "fl-admin") {
				const v = readAlias() || activePartnerAlias || getDefaultPartnerAlias();
				writeAlias(v);
				setActivePartnerAlias(v);
			} else {
				// Non-admin: alias is irrelevant
				clearAlias();
				setActivePartnerAlias(null);
			}
		} catch {
			clearLocal();
		} finally {
			setLoading(false);
		}
	}, [clearLocal, activePartnerAlias]);

	useEffect(() => {
		fetchMe();
	}, [fetchMe]);

	const login = useCallback(
		async ({ email, password }) => {
			const res = await apiPost("/auth/signin", {
				body: { email, password },
				onUnauthorized: clearLocal
			});

			const newToken = res?.data?.token;
			if (!newToken) throw new Error("Login failed: token missing");

			writeToken(newToken);
			setToken(newToken);

			// Fast set from signin response
			const role = res?.data?.role ?? null;
			const partnerName = res?.data?.partnerName ?? null;
			const userEmail = res?.data?.email ?? email ?? null;

			if (role) setUser({ role, partnerName, email: userEmail });

			// Verify / hydrate via /me (recommended once)
			await fetchMe();

			navigate("/dashboard", { replace: true });
		},
		[fetchMe, navigate, clearLocal]
	);

	const logout = useCallback(() => {
		clearLocal();
		navigate("/login", { replace: true });
	}, [navigate, clearLocal]);

	const setAdminPartnerAlias = useCallback((alias) => {
		const v = String(alias || "").trim();
		if (!v) return;
		writeAlias(v);
		setActivePartnerAlias(v);
	}, []);

	const dataPartnerName = useMemo(() => {
		if (!user) return null;
		if (user.role === "fl-admin")
			return activePartnerAlias || getDefaultPartnerAlias();
		return user.partnerName;
	}, [user, activePartnerAlias]);

	const value = useMemo(
		() => ({
			token,
			user,
			role: user?.role || null,
			partnerName: user?.partnerName || null,
			email: user?.email || null,

			activePartnerAlias,
			setAdminPartnerAlias,
			dataPartnerName,

			isAuthenticated: !!user && !!token,
			loading,

			fetchMe,
			login,
			logout
		}),
		[
			token,
			user,
			activePartnerAlias,
			setAdminPartnerAlias,
			dataPartnerName,
			loading,
			fetchMe,
			login,
			logout
		]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
	return ctx;
};
